import { supabase } from '../../lib/supabase';
import type { Profile, Band } from '../../types/database';
import { calculateDistance } from '../../utils/distance';

export type MusicianWithDetails = Profile & {
  bands: (Band & { role: string; instruments: string[] })[];
  distance?: number;
};

export async function fetchMusicianDetails(
  slug: string,
  coordinates?: { latitude: number; longitude: number } | null
): Promise<MusicianWithDetails> {
  try {
    // Fetch musician details with their bands
    const { data: musician, error: musicianError } = await supabase
      .from('profiles')
      .select(`
        *,
        bands:band_members(
          role,
          instruments,
          band:bands!band_members_band_id_fkey(*)
        )
      `)
      .eq('slug', slug)
      .eq('is_musician', true)
      .single();

    if (musicianError) throw musicianError;
    if (!musician) throw new Error('Musician not found');

    // Transform bands data structure
    const bands = musician.bands.map(membership => ({
      ...membership.band,
      role: membership.role,
      instruments: membership.instruments,
    }));

    // Add distance if coordinates are provided
    let distance;
    if (coordinates && musician.coordinates) {
      distance = calculateDistance(coordinates, musician.coordinates);
    }

    return {
      ...musician,
      bands,
      distance,
    } as MusicianWithDetails;
  } catch (error) {
    console.error('Error fetching musician details:', error);
    throw error;
  }
}

export async function updateMusician(
  id: string,
  data: Partial<Profile>
): Promise<Profile> {
  try {
    const { data: updatedMusician, error } = await supabase
      .from('profiles')
      .update({
        ...data,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    if (!updatedMusician) throw new Error('Failed to update musician');

    return updatedMusician;
  } catch (error) {
    console.error('Error updating musician:', error);
    throw error;
  }
}